import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

export type MoreAction = {
  id: string
  label: string
  onSelect: () => void
  danger?: boolean
}

export function MoreActionsMenu({ label, actions }: { label: string; actions: MoreAction[] }) {
  const [open, setOpen] = useState(false)
  const shellRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const focusItem = (position: 'first' | 'last') => {
    window.requestAnimationFrame(() => {
      const items = shellRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
      if (!items?.length) return
      items[position === 'first' ? 0 : items.length - 1]?.focus()
    })
  }

  const openMenu = (position: 'first' | 'last' = 'first') => {
    setOpen(true)
    focusItem(position)
  }

  useEffect(() => {
    if (!open) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !shellRef.current?.contains(event.target)) setOpen(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const navigateItems = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'))
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)
    let nextIndex = currentIndex
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length
    else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = items.length - 1
    else return
    event.preventDefault()
    items[nextIndex]?.focus()
  }

  if (!actions.length) return null

  return (
    <div className="more-actions" ref={shellRef}>
      <button
        ref={triggerRef}
        className="more-actions-trigger"
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openMenu('first')
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            openMenu('last')
          }
        }}
      >
        <svg className="more-actions-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
          <circle cx="5" cy="12" r="1.75" fill="currentColor" />
          <circle cx="12" cy="12" r="1.75" fill="currentColor" />
          <circle cx="19" cy="12" r="1.75" fill="currentColor" />
        </svg>
      </button>
      {open ? (
        <div className="more-actions-menu" role="menu" aria-label={label} onKeyDown={navigateItems}>
          {actions.map((action) => (
            <button
              key={action.id}
              className={action.danger ? 'danger-text' : undefined}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                action.onSelect()
              }}
            >{action.label}</button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
